import { useEffect, useState } from "react";
import type { PropRuntimeGraph } from "../../game/assets/shared/types/PropRuntimeGraph";
import type { BattleManager } from "../../game/assets/pri_wqs/scripts/core/BattleManager";
import type {
  PropRuntimeEvent,
  RuntimePropInstance,
} from "../../game/assets/pri_wqs/scripts/core/propMechanics/PropMechanicsTypes";
import { FixedSlotsPanel } from "./FixedSlotsPanel";

export interface RecipesPanelProps {
  manager?: BattleManager;
  catalog?: PropRuntimeGraph[];
  slotCount: number;
  selectedId: string;
  onSelect: (item?: RuntimePropInstance) => void;
}

export function RecipesPanel({ manager, catalog, slotCount, selectedId, onSelect }: RecipesPanelProps) {
  const [items, setItems] = useState<RuntimePropInstance[]>([]);

  useEffect(() => {
    setItems([]);
    if (!manager) return undefined;

    const eventBus = manager.getEventBus();
    const addOrUpdateItem = (event: PropRuntimeEvent) => {
      if (!event.item || !isRecipeItem(event.item)) return;
      if (!event.item.alive) {
        setItems((prev) => removeItem(prev, event.item!.id));
        return;
      }
      setItems((prev) => upsertItem(prev, event.item!));
    };
    const removeEventItem = (event: PropRuntimeEvent) => {
      if (!event.item || !isRecipeItem(event.item)) return;
      setItems((prev) => removeItem(prev, event.item!.id));
    };

    const unsubs = [
      eventBus.on("propAcquired", addOrUpdateItem),
      eventBus.on("propRemoved", removeEventItem),
    ];

    return () => {
      unsubs.forEach((unsubscribe) => unsubscribe());
    };
  }, [manager]);

  return (
    <FixedSlotsPanel
      title="配方"
      items={items}
      catalog={catalog}
      slotCount={slotCount}
      selectedId={selectedId}
      onSelect={onSelect}
      enableMagicEffectAnchor
    />
  );
}

function isRecipeItem(item: RuntimePropInstance): boolean {
  return item.graph.type === 3;
}

function upsertItem(items: RuntimePropInstance[], item: RuntimePropInstance): RuntimePropInstance[] {
  const index = items.findIndex((current) => current.id === item.id);
  if (index < 0) return [...items, item];
  const next = [...items];
  next[index] = item;
  return next;
}

function removeItem(items: RuntimePropInstance[], id: string): RuntimePropInstance[] {
  return items.filter((item) => item.id !== id);
}
